/**
 * Auth context for managing authentication state globally
 *
 * Subscribes to the module-level session cache in supabase.ts via
 * useSyncExternalStore — no separate onAuthStateChange listener needed.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  ReactNode,
} from "react";
import { subscribeSession, getSessionSnapshot } from "../lib/supabase";

interface AuthContextType {
  session: ReturnType<typeof getSessionSnapshot>;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({
  children,
  onSessionLost,
}: {
  children: ReactNode;
  /** Clears the app's own caches when the session is lost (sign-out or token refresh failure). */
  onSessionLost?: () => void;
}) {
  const session = useSyncExternalStore(subscribeSession, getSessionSnapshot);
  const loading = session === undefined;
  const prevSessionRef = useRef(session);

  useEffect(() => {
    if (prevSessionRef.current && !session) {
      onSessionLost?.();
    }
    prevSessionRef.current = session;
  }, [session, onSessionLost]);

  const value = {
    session,
    loading,
    isAuthenticated: !!session,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
